import React from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  Chip,
  IconButton,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { cashierTransactionTypeFilter } from "../../../utils/cashier";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import Tooltip from "@mui/material/Tooltip";

const TransactionListItem = ({
  transaction,
  formatFinance,
  dateTimeMask,
  setAsResolved,
  remove,
}) => {
  const theme = useTheme();

  // Determine color based on transaction type
  const getTransactionColor = (type) => {
    switch (type) {
      case 1: // Reforço
        return theme.palette.success;
      case 2: // Retirada
        return theme.palette.warning;
      case 3: // Pendência
        return transaction.is_resolved
          ? theme.palette.success
          : theme.palette.error;
      default:
        return theme.palette.info;
    }
  };

  const transactionColor = getTransactionColor(transaction.transaction_type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <ListItem
        component={motion.div}
        whileHover={{
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          boxShadow: theme.shadows[1],
          y: -2,
        }}
        sx={{
          mb: 1,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          transition: "all 0.2s ease-in-out",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Chip
              icon={<MonetizationOnOutlinedIcon fontSize="small" />}
              label={cashierTransactionTypeFilter(transaction.transaction_type)}
              size="small"
              sx={{
                height: 24,
                bgcolor: alpha(transactionColor.main, 0.1),
                color: transactionColor.main,
                fontWeight: 500,
                "& .MuiChip-icon": {
                  color: transactionColor.main,
                },
              }}
            />
            <Typography variant="h6" fontWeight={500}>
              {formatFinance(transaction.amount_cents / 100)}
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {transaction.description || "Sem detalhes"}
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {dateTimeMask(transaction.created_at)}
          </Typography>

          {transaction.transaction_type === 3 && transaction.is_resolved && (
            <Chip
              label="Resolvido"
              size="small"
              sx={{
                mt: 1,
                height: 20,
                fontSize: "0.7rem",
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
              }}
            />
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          {transaction.transaction_type !== 3 && (
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title="Deletar transação">
                <IconButton
                  size="small"
                  onClick={remove}
                  sx={{
                    color: theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    p: 0.75,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.2),
                    },
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </motion.div>
          )}

          {transaction.transaction_type === 3 && !transaction.is_resolved && (
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title="Resolver pendência">
                <IconButton
                  size="small"
                  onClick={setAsResolved}
                  sx={{
                    color: theme.palette.success.main,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    p: 0.75,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.success.main, 0.2),
                    },
                  }}
                >
                  <FactCheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </motion.div>
          )}
        </Stack>
      </ListItem>
    </motion.div>
  );
};

export default TransactionListItem;
